import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from "@angular/core";
import { Comment } from "app/shared/model/app.model";
import { ConfirmationService } from "primeng/api";
import { CommentListService } from "./comment-list.service";
import { CommentEntity, CommentEntityMetadata } from "./comment-list.model";
import { EditableColumn } from "primeng/table";
import { LogService } from "../../logs/log.service";
import { AppStateService } from "../../core/services/app-state.service";

@Component({
	selector: 'lb-comment-list[commentEntity]',
	templateUrl: './comment-list.component.html',
	styleUrls: ['./comment-list.component.css'],
	providers: [CommentListService]
})
export class CommentListComponent implements OnInit {
	@Input()
	get commentEntity() {
		return this._commentEntity;
	}
	set commentEntity(val) {
		this._commentEntity = val;
		this.setup();
	}
	
	@Output() commentsModified = new EventEmitter();

	comments!: Comment[];
	headerMessage = `${AppStateService.screenAction} cell to edit comments you have created`;

	@ViewChildren(EditableColumn) editableColumns!: QueryList<EditableColumn>

	get readOnlyUser() {
		return LogService.isReadOnlyUser;
	}

	get userId() {
		return AppStateService.currentUser?.id;
	}

	get role() {
		return LogService.selectedLog?.role;
	}

	private currentEditableColumn?: EditableColumn;
	private _commentEntity!: CommentEntity;
	private hadInit = false;

	constructor(
		private confirmationService: ConfirmationService,
		private commentListService: CommentListService
	) { }

	ngOnInit() {
		this.hadInit = true;
		this.setup();
	}

	onNewCommentClicked() {
		this.comments.push(CommentEntity.newComment());

		// Necessary to open cell editor programmatically (no way to do that in public API)
		setTimeout(() => this.editableColumns.last.openCell(), 0);
	}

	onDeleteClicked(comment: Comment, index: number, event: Event) {
		if (comment.id) {
			this.confirmationService.confirm({
				target: event.target!,
				message: 'Are you sure you want to delete this comment?',
				header: 'Confirm',
				icon: 'pi pi-exclamation-circle',
				accept: () => this.removeComment(comment, index)
			});
		}
		else
			this.comments.splice(index, 1)
	}

	onEditInit(event: any) {
		this.currentEditableColumn = this.editableColumns.find(ec => ec.data == event.data);
	}

	onEditCancel(event: any) {
		event.data.comment = event.field;

		this.currentEditableColumn = undefined;
	}

	onEditComplete(event: any) {
		const comment: Comment = event.data;
		
		if (comment.comment?.trim()) {
			const hasChanges = comment.comment != event.field;
			
			if (hasChanges) {
				const refreshComment = (comment: Comment) => {
					this.comments[event.index] = comment;
					this.markCommentsModified();
				}

				comment.id ?
					this.commentListService.updateComment(comment).subscribe(refreshComment) :
					this.commentListService.addComment(comment).subscribe(refreshComment);
			}
		}
		else
			this.removeComment(comment, event.index);
		
		this.currentEditableColumn = undefined;
	}

	onTextareaFocusOut(event: FocusEvent) {
		this.currentEditableColumn?.closeEditingCell(true, event);
	}
	
	private setup() {
		if (!this.hadInit) return;
		this.comments = CommentEntity.getComments(this.commentEntity);
		this.commentListService.entityMetadata = new CommentEntityMetadata(this.commentEntity);
	}
	
	private markCommentsModified() {
		this.commentsModified.next();
	}

	private removeComment(comment: Comment, index: number) {
		if (comment.id) {
			this.commentListService.deleteComment(comment).subscribe(() => {
				if (comment.id !== this.comments[index]?.id) return;
				
				this.comments.splice(index, 1);
				this.markCommentsModified();
			});
		}
		else
			this.comments.splice(index, 1);
	}
}